/**
 * Carnet en ligne (Académie) — l'élève à distance inscrit ses RÉPÉTITIONS par technique (pas d'auto-évaluation) :
 * totaux par technique / ceinture / semaine, journal daté, et demande d'évaluation de ceinture au Kru.
 */
import { useCallback, useEffect, useState } from "react";
import { memberApi } from "../../lib/api";
import { toast } from "sonner";
import { beltHex } from "../../lib/beltColor";
import { BeltEvaluationPanel } from "./BeltEvaluationPanel";

const QUICK = [10, 25, 50];

export function OnlineCarnet({ code, discName, beltGroups, currentBelt }) {
  const [reps, setReps] = useState(null);
  const [evalInfo, setEvalInfo] = useState(null);
  const [openBelt, setOpenBelt] = useState(currentBelt || beltGroups?.[0]?.belt || "");
  const load = useCallback(() => {
    memberApi.get(`/member/academy/reps/${code}`).then((r) => setReps(r.data)).catch(() => setReps({ total: 0, week_total: 0, by_technique: {}, by_belt: {}, journal: [] }));
    memberApi.get(`/member/academy/evaluation/${code}`).then((r) => setEvalInfo(r.data)).catch(() => setEvalInfo(null));
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const add = async (t, n) => {
    if (!n || n < 1) return;
    try {
      const r = await memberApi.post("/member/academy/reps", { discipline: code, technique_id: t.id, reps: n });
      setReps((p) => ({ ...p, ...r.data.summary }));
      toast.success(`+${n} répétitions — ${t.name}`);
      (r.data.summary.new_badges || []).forEach((b) => toast.success(`${b.icon} Badge débloqué : ${b.label} — ${b.desc}`, { duration: 8000 }));
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => { try { await memberApi.delete(`/member/academy/reps/${id}`); load(); } catch { toast.error("Erreur"); } };

  if (!reps) return <p className="text-white/40 text-sm">Chargement de ton carnet…</p>;
  const minReps = evalInfo?.min_reps_per_technique || 300;
  return (
    <div className="space-y-5" data-testid="online-carnet">
      <div className="border border-[#C9A961]/40 bg-[#C9A961]/[0.06] p-4">
        <p className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A961]">🥋 Carnet en ligne · {discName}</p>
        <p className="text-white/70 text-xs mt-1">Tu t'entraînes à distance : inscris tes <b>répétitions</b> après chaque séance. Le Kru valide ta ceinture lors d'une évaluation (voir plus bas). Objectif : <b>{minReps} répétitions</b> par technique de la ceinture visée.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Répétitions totales", reps.total, "online-stat-total"], ["Cette semaine", reps.week_total, "online-stat-week"], ["Séances inscrites", reps.sessions || 0, "online-stat-sessions"], ["Jours d'affilée", reps.streak_days || 0, "online-stat-streak"]].map(([l, v, tid]) => (
          <div key={l} className="border border-white/10 bg-white/[0.02] p-3"><div className="kz-display text-2xl text-[#C9A961]" data-testid={tid}>{v}</div><div className="kz-mono text-[9px] tracking-wider uppercase text-white/40">{l}</div></div>
        ))}
      </div>

      <WeeklyBadges reps={reps} />

      <div>
        <p className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-2">Progression par ceinture (répétitions)</p>
        <div className="space-y-2">
          {(beltGroups || []).map((g) => {
            const done = reps.by_belt?.[g.belt] || 0;
            const target = Math.max(1, g.techniques.length * minReps);
            const pct = Math.min(100, Math.round((done / target) * 100));
            const open = openBelt === g.belt;
            return (
              <div key={g.belt} className={`border ${open ? "border-[#C9A961]/60" : "border-white/10"}`} data-testid={`online-belt-${g.belt}`}>
                <button onClick={() => setOpenBelt(open ? "" : g.belt)} className="w-full text-left px-3 py-2.5" data-testid={`online-belt-toggle-${g.belt}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-3 h-3 rounded-full border border-white/30" style={{ background: beltHex(g.belt) }} />
                    <span className="text-white font-medium">{g.belt}</span>
                    {g.belt === currentBelt && <span className="kz-mono text-[9px] text-[#C9A961] border border-[#C9A961]/50 px-1.5 py-0.5">ACTUELLE</span>}
                    <span className="text-white/40 text-xs">{g.techniques.length} techniques</span>
                    <span className="ml-auto text-white/70 text-xs" data-testid={`online-belt-reps-${g.belt}`}>{done} / {target} rép. · {pct}%</span>
                  </div>
                  <div className="h-1.5 bg-white/10 mt-2 overflow-hidden"><div className="h-full transition-all" style={{ width: `${pct}%`, background: beltHex(g.belt) }} /></div>
                </button>
                {open && (
                  <div className="divide-y divide-white/5 border-t border-white/10">
                    {g.techniques.map((t) => <TechReps key={t.id} t={t} total={reps.by_technique?.[t.id] || 0} min={minReps} onAdd={(n) => add(t, n)} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {(reps.journal || []).length > 0 && (
        <div data-testid="online-journal">
          <p className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-2">Journal (dernières entrées)</p>
          <ul className="border border-white/10 divide-y divide-white/5 text-xs max-h-56 overflow-auto">
            {reps.journal.map((j) => (
              <li key={j.id} className="flex items-center gap-2 px-3 py-1.5 text-white/70" data-testid={`online-journal-${j.id}`}>
                <span className="kz-mono text-white/40">{j.date}</span><span className="flex-1">{j.technique_name} <span className="text-white/35">· {j.belt}</span></span>
                <b className="text-[#C9A961]">+{j.reps}</b>
                <button onClick={() => remove(j.id)} className="text-white/30 hover:text-rose-400 px-1" title="Retirer" data-testid={`online-journal-del-${j.id}`}>✕</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Leaderboard code={code} refreshKey={reps.total} />

      {evalInfo && <BeltEvaluationPanel code={code} info={evalInfo} beltGroups={beltGroups} currentBelt={currentBelt} onChanged={load} />}
    </div>
  );
}

function TechReps({ t, total, min, onAdd }) {
  const [custom, setCustom] = useState("");
  const pct = Math.min(100, Math.round((total / Math.max(1, min)) * 100));
  return (
    <div className="px-3 py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`online-tech-${t.id}`}>
      <div className="flex-1 min-w-[160px]">
        <div className="text-white/85">{t.name} {t.category && <span className="kz-mono text-[9px] text-white/35 uppercase ml-1">{t.category}</span>}</div>
        <div className="flex items-center gap-2 mt-1"><div className="h-1 flex-1 bg-white/10 overflow-hidden"><div className={`h-full ${pct >= 100 ? "bg-emerald-400" : "bg-[#C9A961]"}`} style={{ width: `${pct}%` }} /></div><span className="kz-mono text-[10px] text-white/50" data-testid={`online-tech-total-${t.id}`}>{total} rép.</span></div>
      </div>
      <div className="flex items-center gap-1">
        {QUICK.map((n) => <button key={n} onClick={() => onAdd(n)} className="px-2 py-1 text-[11px] text-white/70 border border-white/15 hover:border-[#C9A961] hover:text-[#C9A961]" data-testid={`online-add-${n}-${t.id}`}>+{n}</button>)}
        <input type="number" min="1" max="5000" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="n" className="w-14 bg-black text-white border border-white/15 px-1.5 py-1 text-[11px]" data-testid={`online-custom-${t.id}`} />
        <button onClick={() => { onAdd(Number(custom)); setCustom(""); }} disabled={!custom} className="px-2 py-1 text-[11px] bg-[#C9A961] text-black font-semibold disabled:opacity-30" data-testid={`online-add-custom-${t.id}`}>OK</button>
      </div>
    </div>
  );
}

function WeeklyBadges({ reps }) {
  if (!reps.weeks) return null;
  const goal = reps.weekly_goal || 150;
  return (
    <div className="border border-white/10 bg-white/[0.02] p-3" data-testid="weekly-badges">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <p className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A961]">Objectif hebdo · {goal} répétitions</p>
          <div className="flex items-end gap-1 mt-2 h-10" data-testid="weekly-bars">
            {reps.weeks.map((w) => (
              <div key={w.week} title={`${w.start} : ${w.total} rép.`} className="flex-1 flex flex-col items-center justify-end h-full">
                <div className={`w-full ${w.met ? "bg-emerald-400" : w.current ? "bg-[#C9A961]/60" : "bg-white/15"}`} style={{ height: `${Math.max(6, Math.min(100, (w.total / Math.max(goal, 1)) * 100))}%` }} data-testid={`weekly-bar-${w.week}`} />
              </div>
            ))}
          </div>
          <p className="text-white/50 text-[10px] mt-1">8 dernières semaines · vert = objectif atteint · <b className="text-white/80" data-testid="perfect-streak">{reps.perfect_streak}</b> semaine(s) parfaite(s) d'affilée</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="badge-list">
          {(reps.badges || []).map((b) => (
            <span key={b.code} className="inline-flex items-center gap-1.5 border border-[#C9A961]/60 bg-[#C9A961]/10 px-2.5 py-1.5 text-xs text-white" title={b.desc} data-testid={`badge-${b.code}`}><span className="text-base">{b.icon}</span>{b.label}</span>
          ))}
          {reps.next_badge && <span className="inline-flex items-center gap-1.5 border border-dashed border-white/20 px-2.5 py-1.5 text-xs text-white/40" data-testid="badge-next">{reps.next_badge.icon} {reps.next_badge.label} · encore {Math.max(0, reps.next_badge.need - reps.perfect_streak)} sem.</span>}
        </div>
      </div>
    </div>
  );
}

function Leaderboard({ code, refreshKey }) {
  const [lb, setLb] = useState(null);
  useEffect(() => { memberApi.get(`/member/academy/leaderboard/${code}`).then((r) => setLb(r.data)).catch(() => setLb(null)); }, [code, refreshKey]);
  if (!lb) return null;
  const monthLabel = new Date(lb.month + "-01T12:00:00").toLocaleDateString("fr-CA", { month: "long", year: "numeric" });
  return (
    <div className="border border-white/10 bg-white/[0.02] p-3" data-testid="leaderboard">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A961]">🏆 Classement du mois · {monthLabel}</p>
        <span className="text-white/50 text-[10px]" data-testid="leaderboard-participants">{lb.participants} participant(s) · anonyme</span>
      </div>
      {lb.challenge && <p className="text-[11px] text-white/60 mt-1 border border-[#C9A961]/30 bg-[#C9A961]/[0.05] px-2 py-1" data-testid="leaderboard-challenge">🎯 {lb.challenge.text} {lb.challenge.podium.map((p) => p.icon).join(" ")}</p>}
      {lb.rows.length === 0 ? <p className="text-white/40 text-xs mt-2">Personne n'a encore inscrit de répétitions ce mois-ci — sois le premier !</p> : (
        <ol className="mt-2 space-y-1 text-xs" data-testid="leaderboard-rows">
          {lb.rows.map((r) => (
            <li key={r.rank} className={`flex items-center gap-2 px-2 py-1 ${r.me ? "bg-[#C9A961]/15 border border-[#C9A961]/50 text-white" : "text-white/70"}`} data-testid={`leaderboard-row-${r.rank}`}>
              <span className="w-6 text-right kz-mono">{r.rank <= 3 ? ["🥇", "🥈", "🥉"][r.rank - 1] : `${r.rank}.`}</span>
              <span className="flex-1">{r.alias}</span>
              <span className="text-white/40">{r.sessions} séance(s)</span>
              <b className={r.me ? "text-[#C9A961]" : ""}>{r.total} rép.</b>
            </li>
          ))}
        </ol>
      )}
      {lb.me && lb.my_rank > 10 && <p className="text-xs text-[#C9A961] mt-2" data-testid="leaderboard-me">Toi : {lb.my_rank}e sur {lb.participants} · {lb.me.total} rép.</p>}
      {!lb.me && lb.rows.length > 0 && <p className="text-xs text-white/40 mt-2">Inscris tes répétitions pour apparaître au classement.</p>}
    </div>
  );
}
